import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, Loader2, Sparkles, ThumbsUp, ThumbsDown, History, PauseCircle, PlayCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { reglasApi } from '../IADashboard/entregasV3';
import { photoUrl } from '../IADashboard/iaData';
import { useObraTimeZone } from '../../../obras/hooks/useObraTimeZone';

/* Reglas "Avisame cuando…" del adicional Alertas de entregas, por cámara (entregasReglas/{feedId}).
   Predefinidas = salen de lo que detecta el modelo (iaV3). Personalizada (beta) = texto libre que evaluamos con IA
   1-2 veces por día mirando la primera y la última foto. Abajo, el historial de avisos con fotos y 👍/👎. */

const inputCls = 'border border-gray-300 rounded-lg px-2 py-1 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400';
const errorMsg = (e) => e?.response?.data?.message || e?.message || 'Algo salió mal';
const nueva = (tipo, disparador) => ({
  id: '', tipo, ...(tipo === 'libre' ? { texto: '' } : { disparador }), activa: true, desde: null, hasta: null, confMin: 'media',
  canales: { wsp: true, mail: true }, silencio: { activo: false, desde: '12:00', hasta: '13:00' },
});
const fechaHora = (iso, timeZone) => { try { return new Date(iso).toLocaleString('es-AR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: timeZone || 'America/Argentina/Buenos_Aires' }); } catch (e) { return ''; } };

function Foto({ f, label }) {
  const [url, setUrl] = useState(null);
  useEffect(() => { let a = true; if (f?.key) photoUrl({ s3: f.key }).then((u) => a && setUrl(u)).catch(() => {}); return () => { a = false; }; }, [f?.key]);
  return (
    <div className="min-w-0">
      <div className="aspect-video bg-gray-100 rounded-md overflow-hidden">{url && <img src={url} alt={label} className="w-full h-full object-cover" loading="lazy" />}</div>
      <p className="text-[11px] text-gray-500 mt-0.5">{label}</p>
    </div>
  );
}

const resumen = (r) => [
  r.desde && r.hasta ? `de ${r.desde} a ${r.hasta}` : 'todo el día',
  r.tipo === 'predef' ? `confianza ${r.confMin}` : null,
  [r.canales.wsp && 'WhatsApp', r.canales.mail && 'mail'].filter(Boolean).join(' y ') || 'sin canal',
  r.silencio.activo ? `sin avisos de ${r.silencio.desde} a ${r.silencio.hasta}` : null,
].filter(Boolean).join(' · ');

function Regla({ r, titulo, onChange, onQuitar, evaluacion }) {
  const set = (patch) => onChange({ ...r, ...patch });
  const ventana = !!(r.desde && r.hasta);
  const [abierta, setAbierta] = useState(r.tipo === 'libre' && !r.texto);
  return (
    <div className={`border rounded-xl p-3 ${r.activa ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50 opacity-75'}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          {r.tipo === 'libre' ? (
            <>
              <p className="text-xs font-semibold text-violet-700 inline-flex items-center gap-1"><Sparkles size={12} />Regla personalizada (beta)</p>
              <div className="flex items-center gap-1.5 mt-1">
                <span className="text-sm text-gray-700 shrink-0">Avisame</span>
                <input value={r.texto} onChange={(e) => set({ texto: e.target.value })} maxLength={200} placeholder="cuando desencofren la losa sobre planta baja" className={`${inputCls} flex-1 min-w-0`} aria-label="Texto de la regla" />
              </div>
              <p className="text-[11px] text-gray-500 mt-1">La revisamos con IA 1 o 2 veces por día (13 y 18 h) comparando la primera y la última foto; puede equivocarse. Solo avisamos si está segura. Incluido en el adicional.</p>
              {evaluacion && (
                <p className="text-[11px] mt-1 text-gray-600">Última revisión: {evaluacion.dia?.slice(8, 10)}/{evaluacion.dia?.slice(5, 7)} {evaluacion.hora} · {evaluacion.error ? 'no se pudo revisar' : `${evaluacion.cumple ? 'sí' : 'no'} (confianza ${evaluacion.confianza}) — ${evaluacion.motivo}`}</p>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-800"><span className="text-gray-500">Avisame cuando</span> <b>{titulo}</b></p>
          )}
          <button type="button" onClick={() => setAbierta(!abierta)} className="text-[11px] text-gray-500 hover:text-blue-700 mt-0.5 text-left">{resumen(r)} · <span className="font-semibold text-blue-700">{abierta ? 'listo' : 'cambiar'}</span></button>
        </div>
        <button type="button" onClick={() => set({ activa: !r.activa })} title={r.activa ? 'Pausar' : 'Reanudar'} className="p-1 text-gray-500 hover:text-gray-800">{r.activa ? <PauseCircle size={17} /> : <PlayCircle size={17} />}</button>
        <button type="button" onClick={onQuitar} title="Quitar" className="p-1 text-red-500 hover:text-red-700"><Trash2 size={16} /></button>
      </div>
      {abierta && <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-2 text-xs text-gray-600">
        <label className="inline-flex items-center gap-1.5">
          <input type="checkbox" className="accent-blue-600" checked={ventana} onChange={(e) => set(e.target.checked ? { desde: '07:00', hasta: '12:00' } : { desde: null, hasta: null })} />Solo de
          <input type="time" disabled={!ventana} value={r.desde || '07:00'} onChange={(e) => e.target.value && set({ desde: e.target.value })} className={`${inputCls} w-[6rem]`} aria-label="Desde" />a
          <input type="time" disabled={!ventana} value={r.hasta || '12:00'} onChange={(e) => e.target.value && set({ hasta: e.target.value })} className={`${inputCls} w-[6rem]`} aria-label="Hasta" />
        </label>
        {r.tipo === 'predef' && (
          <label className="inline-flex items-center gap-1.5">Confianza mínima
            <select value={r.confMin} onChange={(e) => set({ confMin: e.target.value })} className={inputCls}><option value="media">media</option><option value="alta">alta</option></select>
          </label>
        )}
        <label className="inline-flex items-center gap-1"><input type="checkbox" className="accent-blue-600" checked={r.canales.wsp} onChange={(e) => set({ canales: { ...r.canales, wsp: e.target.checked } })} />WhatsApp</label>
        <label className="inline-flex items-center gap-1"><input type="checkbox" className="accent-blue-600" checked={r.canales.mail} onChange={(e) => set({ canales: { ...r.canales, mail: e.target.checked } })} />Mail</label>
        <label className="inline-flex items-center gap-1.5">
          <input type="checkbox" className="accent-blue-600" checked={r.silencio.activo} onChange={(e) => set({ silencio: { ...r.silencio, activo: e.target.checked } })} />Sin avisos de
          <input type="time" disabled={!r.silencio.activo} value={r.silencio.desde} onChange={(e) => e.target.value && set({ silencio: { ...r.silencio, desde: e.target.value } })} className={`${inputCls} w-[6rem]`} aria-label="Silencio desde" />a
          <input type="time" disabled={!r.silencio.activo} value={r.silencio.hasta} onChange={(e) => e.target.value && set({ silencio: { ...r.silencio, hasta: e.target.value } })} className={`${inputCls} w-[6rem]`} aria-label="Silencio hasta" />
        </label>
      </div>}
    </div>
  );
}

export default function ReglasEntregas({ feedID }) {
  const obraTimeZone = useObraTimeZone(feedID);
  const [d, setD] = useState(null);
  const [reglas, setReglas] = useState([]);
  const [sucio, setSucio] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [avisos, setAvisos] = useState(null);
  const [agregar, setAgregar] = useState('');

  const cargar = useCallback(() => {
    reglasApi.leer(feedID).then((r) => { setD(r); setReglas(r.reglas); setSucio(false); }).catch((e) => toast.error(errorMsg(e)));
    reglasApi.avisos(feedID).then(setAvisos).catch(() => setAvisos([]));
  }, [feedID]);
  useEffect(() => { if (feedID) cargar(); }, [feedID, cargar]);

  if (!d) return <div className="flex items-center gap-2 text-sm text-gray-500 mt-4"><Loader2 size={15} className="animate-spin" />Cargando reglas…</div>;
  const titulos = Object.fromEntries(d.disparadores.map((x) => [x.key, x.titulo]));
  const cambiar = (i, r) => { setReglas(reglas.map((x, j) => (j === i ? r : x))); setSucio(true); };
  const quitar = (i) => { setReglas(reglas.filter((_, j) => j !== i)); setSucio(true); };
  const sumar = (v) => {
    if (!v) return;
    setReglas([...reglas, v === 'libre' ? nueva('libre') : nueva('predef', v)]);
    setSucio(true); setAgregar('');
  };
  const guardar = async () => {
    setGuardando(true);
    try { const r = await reglasApi.guardar(feedID, reglas); setReglas(r.reglas); setSucio(false); toast.success('Reglas guardadas. Rigen desde la próxima revisión (cada 20 min).'); } catch (e) { toast.error(errorMsg(e)); }
    setGuardando(false);
  };
  const votar = async (a, voto) => {
    const v = a.voto === voto ? null : voto;
    setAvisos(avisos.map((x) => (x.id === a.id ? { ...x, voto: v } : x)));
    reglasApi.votar(feedID, a.id, v).catch(() => toast.error('No se pudo guardar tu respuesta'));
  };
  const libres = reglas.filter((r) => r.tipo === 'libre').length;

  return (
    <div className="mt-4 pt-4 border-t border-gray-100 space-y-3" data-reglas-entregas>
      <div>
        <h4 className="text-sm font-semibold text-gray-800">Avisame cuando…</h4>
        <p className="text-xs text-gray-500">Elegí qué te importa de esta obra. Llegan a los números prendidos arriba, entre las 8 y las 20 h.{d.porDefecto ? ' Estas son las reglas recomendadas: cambialas a tu gusto.' : ''}</p>
      </div>
      {reglas.map((r, i) => (
        <Regla key={r.id || `n${i}`} r={r} titulo={titulos[r.disparador]} evaluacion={d.evaluaciones?.[r.id]} onChange={(x) => cambiar(i, x)} onQuitar={() => quitar(i)} />
      ))}
      {!d.habilitado && <p className="text-xs text-amber-800 bg-amber-50 rounded-lg px-3 py-2">Para guardar reglas tenés que tener el adicional activo en esta cámara.</p>}
      <div className="flex flex-wrap items-center gap-2">
        <select value={agregar} onChange={(e) => sumar(e.target.value)} className={`${inputCls} w-full sm:w-auto`} aria-label="Sumar una regla" disabled={reglas.length >= d.limites.reglas}>
          <option value="">+ Sumar una regla…</option>
          {d.disparadores.map((x) => <option key={x.key} value={x.key}>Avisame cuando {x.titulo}</option>)}
          <option value="libre" disabled={libres >= d.limites.libres}>Regla personalizada (beta): escribila vos</option>
        </select>
        <button type="button" disabled={!sucio || guardando || !d.habilitado} onClick={guardar} className="ml-auto inline-flex items-center gap-1.5 text-sm font-semibold rounded-lg px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
          {guardando ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} className="hidden" />}{sucio ? 'Guardar reglas' : 'Guardado'}
        </button>
      </div>

      <div className="pt-2">
        <h4 className="text-sm font-semibold text-gray-800 inline-flex items-center gap-1.5"><History size={15} />Avisos que salieron</h4>
        {!avisos ? <p className="text-xs text-gray-500 mt-1">Cargando…</p> : !avisos.length ? <p className="text-xs text-gray-500 mt-1">Todavía no salió ningún aviso de esta cámara.</p> : (
          <div className="space-y-3 mt-2">
            {avisos.slice(0, 5).map((a) => (
              <div key={a.id} className="border border-gray-200 rounded-xl p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800">{a.texto}</p>
                    <p className="text-[11px] text-gray-500">{fechaHora(a.at, obraTimeZone)} · regla: {a.tipo === 'libre' ? `«${a.reglaTitulo}» (IA)` : a.reglaTitulo}{a.simulado ? ' · simulado en local' : ''}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button type="button" onClick={() => votar(a, 'up')} title="Estuvo bien" className={`p-1.5 rounded-lg ${a.voto === 'up' ? 'bg-emerald-100 text-emerald-700' : 'text-gray-400 hover:bg-gray-100'}`}><ThumbsUp size={15} /></button>
                    <button type="button" onClick={() => votar(a, 'down')} title="No era / no me sirvió" className={`p-1.5 rounded-lg ${a.voto === 'down' ? 'bg-red-100 text-red-700' : 'text-gray-400 hover:bg-gray-100'}`}><ThumbsDown size={15} /></button>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {a.fotos?.antes && <Foto f={a.fotos.antes} label="Antes" />}
                  {a.fotos?.llegada && <Foto f={a.fotos.llegada} label="Llegada" />}
                  {a.fotos?.despues && <Foto f={a.fotos.despues} label={a.tipo === 'libre' ? 'Ahora' : 'Después'} />}
                </div>
              </div>
            ))}
            <p className="text-[11px] text-gray-500">Tus 👍/👎 nos ayudan a que el modelo aprenda qué es una entrega en tu obra.</p>
          </div>
        )}
      </div>
    </div>
  );
}
